import React, { memo, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { StarIcon, MapPinIcon } from '@heroicons/react/24/solid';

/**
 * VenueCard - Individual venue card for search results
 * Design: Image left (100x100), info right, horizontal layout
 */
const VenueCard = ({ venue, userLocation }) => {
  const navigate = useNavigate();

  // Extract venue data
  const venueId = venue._id || venue.id;
  const venueName = venue.name || 'Unknown Venue';
  const category = venue.venue_category_display || 'Venue';
  const city = venue.city || '';
  const rating = venue.google_rating || 0;
  const distance = venue.distance != null ? parseFloat(venue.distance) : null; // Neon returns numeric as string
  
  // Image URL (first image or fallback)
  const imageUrl = venue.images?.[0] ||
                   venue.first_image_url ||
                   'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400&h=300&fit=crop';

  // Category badge color
  const categoryColor = useMemo(() => {
    const cat = category.toLowerCase();
    if (cat.includes('cafe') || cat.includes('coffee')) return '#dbeafe';
    if (cat.includes('restaurant')) return '#fef3c7';
    if (cat.includes('bar') || cat.includes('pub')) return '#fce7f3';
    if (cat.includes('gym') || cat.includes('fitness')) return '#dcfce7';
    return '#f3f4f6';
  }, [category]);

  const categoryTextColor = useMemo(() => {
    const cat = category.toLowerCase();
    if (cat.includes('cafe') || cat.includes('coffee')) return '#1d4ed8';
    if (cat.includes('restaurant')) return '#d97706';
    if (cat.includes('bar') || cat.includes('pub')) return '#be185d';
    if (cat.includes('gym') || cat.includes('fitness')) return '#166534';
    return '#4b5563';
  }, [category]);

  // Distance display
  const distanceDisplay = useMemo(() => {
    if (!distance) return null;
    if (distance < 1) return `${(distance * 1000).toFixed(0)}m`;
    return `${distance.toFixed(1)}km`;
  }, [distance]);

  // Navigate to venue details — use slug when available, fall back to ObjectId
  const handleClick = useCallback(() => {
    navigate(`/venue/${venue.slug || venueId}`);
  }, [navigate, venue.slug, venueId]);

  return (
    <motion.div
      onClick={handleClick}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02, boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)' }}
      whileTap={{ scale: 0.98 }}
      style={{
        display: 'flex',
        width: '100%',
        maxWidth: '100%',
        minWidth: '280px',
        height: '100px',
        backgroundColor: 'white',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        cursor: 'pointer',
        transition: 'box-shadow 0.2s ease',
        marginBottom: '8px',
        flexShrink: 0
      }}
      data-testid={`venue-card-${venueId}`}
    >
      {/* Left: Image */}
      <div style={{
        width: '100px',
        height: '100px',
        flexShrink: 0,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#f3f4f6'
      }}>
        <img
          src={imageUrl}
          alt={venueName}
          loading="lazy"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
        
        {/* Category badge on image */}
        <div style={{
          position: 'absolute',
          bottom: '6px',
          left: '6px',
          backgroundColor: categoryColor,
          color: categoryTextColor,
          fontSize: '10px',
          fontWeight: '600',
          padding: '3px 8px',
          borderRadius: '12px',
          backdropFilter: 'blur(4px)'
        }}>
          {category}
        </div>
      </div>

      {/* Right: Info */}
      <div style={{
        flex: 1,
        padding: '12px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}>
        {/* Venue Name */}
        <div style={{
          fontWeight: '700',
          fontSize: '14px',
          color: '#111827',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {venueName}
        </div>

        {/* City */}
        {city && (
          <div style={{
            fontSize: '11px',
            color: '#6b7280',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            marginTop: '2px'
          }}>
            <MapPinIcon style={{ width: 12, height: 12 }} />
            {city}
          </div>
        )}

        {/* Bottom Row: Rating, WiFi, Distance, Price */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '11px',
          marginTop: '4px'
        }}>
          {/* Rating */}
          {rating > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
              color: '#f59e0b'
            }}>
              <StarIcon style={{ width: 12, height: 12 }} />
              <span style={{ fontWeight: '600', color: '#374151' }}>{rating.toFixed(1)}</span>
            </div>
          )}

          {/* Distance */}
          {distanceDisplay && (
            <div style={{
              color: '#6b7280',
              fontWeight: '500'
            }}>
              {distanceDisplay}
            </div>
          )}

          {/* field removed - no Neon equivalent yet for price level */}
        </div>
      </div>
    </motion.div>
  );
};

export default memo(VenueCard);
